import React, { useMemo } from 'react';
import { Alert, Box, TextField, FormControlLabel, Checkbox, Typography, Divider } from '@mui/material';
import { ProductSelect } from './selectors/ProductSelect';
import { BundleSelect } from './selectors/BundleSelect';
import { VariantSelect } from './selectors/VariantSelect';
import { SlotSelect } from './selectors/SlotSelect';
import { ModifierSelect } from './selectors/ModifierSelect';
import { WeightSelect } from './selectors/WeightSelect';
import { WeightPriceRanges } from './selectors/WeightPriceRanges';
import { ProductVariantRows } from './selectors/ProductVariantRows';
import { NamedPriceSelect } from './selectors/NamedPriceSelect';
import { formatCents, parseCentsInput } from '../utils/namedPriceUtils';
interface OperationFormProps {
  fields: any;
  handlers: any;
  mode?: 'create' | 'edit' | 'remove';
  menu?: any;
}
export function OperationForm({ fields, handlers, mode = 'create' }: OperationFormProps) {
  // Create a wrapper function to update product with selected variant
  const handleVariantChange = (variants: any) => {
    // When a variant is selected, attach it to the product object so child components can access it
    if (fields.product && variants && variants.length > 0) {
      const selectedVariant = variants[0]; // Use the first selected variant
      
      // Create a new product object with the selected variant
      const updatedProduct = {
        ...fields.product,
        selectedVariant // Add this property to the product
      };
      
      // Update the product in the form state
      handlers.setProduct(updatedProduct);
      
      // Also update the variants in the form state
      handlers.setVariants(variants);
      
      // Clear dependent fields when variant changes
      handlers.setSlot(null);
      handlers.setModifier([]);
      handlers.setWeights([]);
    } else {
      // Just update variants if no product is selected
      handlers.setVariants(variants);
      
      // Clear dependent fields
      handlers.setSlot(null);
      handlers.setModifier([]);
      handlers.setWeights([]);
    }
  };
  
  // Handler to clear all related fields when product changes
  const handleProductChange = () => {
    // Clear variants, slot, modifiers, and weights when product changes
    handlers.setVariants([]);
    handlers.setSlot(null);
    handlers.setModifier([]);
    handlers.setWeights([]);
  };
  
  // Handler to clear dependent fields when slot changes
  const handleSlotChange = (slot: any) => {
    handlers.setSlot(slot);
    handlers.setModifier([]);
    handlers.setWeights([]);
  };

  // --- Intersection helpers for multi-row product+variant operations ---
  const intersectedSlots = useMemo(() => {
    const rows: any[] = fields.productVariantRows ?? [];
    const allVariants = rows.flatMap((r: any) => r.variants ?? []);
    if (allVariants.length === 0) return null;
    const sets = allVariants.map((v: any) => {
      const ids = new Set<string>((v.slots ?? []).map((s: any) => s.slot?.slotId).filter(Boolean));
      return { slots: (v.slots ?? []).map((s: any) => s.slot).filter(Boolean), ids };
    });
    const sharedIds = Array.from(sets[0].ids).filter(id => sets.every(s => s.ids.has(id)));
    return sets[0].slots.filter((s: any) => sharedIds.includes(s.slotId));
  }, [fields.productVariantRows]);

  const intersectedModifiers = useMemo(() => {
    const rows: any[] = fields.productVariantRows ?? [];
    const slotId = fields.slot?.slotId;
    if (!slotId) return [];
    const allVariants = rows.flatMap((r: any) => r.variants ?? []);
    if (allVariants.length === 0) return [];
    const sets = allVariants.map((v: any) => {
      const entry = (v.slots ?? []).find((s: any) => s.slot?.slotId === slotId);
      const mods = (entry?.modifiers ?? []).map((m: any) => m.modifier).filter(Boolean);
      return { mods, ids: new Set<string>(mods.map((m: any) => m.modifierId)) };
    });
    const sharedIds = Array.from(sets[0].ids).filter(id => sets.every(s => s.ids.has(id)));
    return sets[0].mods.filter((m: any) => sharedIds.includes(m.modifierId));
  }, [fields.productVariantRows, fields.slot?.slotId]);

  const intersectedWeights = useMemo(() => {
    const rows: any[] = fields.productVariantRows ?? [];
    const slotId = fields.slot?.slotId;
    if (!slotId) return [];
    const allVariants = rows.flatMap((r: any) => r.variants ?? []);
    if (allVariants.length === 0) return [];
    const sets = allVariants.map((v: any) => {
      const entry = (v.slots ?? []).find((s: any) => s.slot?.slotId === slotId);
      const ws: any[] = [];
      (entry?.modifiers ?? []).forEach((mi: any) => (mi.weights ?? []).forEach((wi: any) => wi.weight && ws.push(wi.weight)));
      const unique = ws.filter((w, i, a) => a.findIndex((x: any) => x.modifierWeightId === w.modifierWeightId) === i);
      return { weights: unique, ids: new Set<string>(unique.map((w: any) => w.modifierWeightId)) };
    });
    const sharedIds = Array.from(sets[0].ids).filter(id => sets.every(s => s.ids.has(id)));
    return sets[0].weights.filter((w: any) => sharedIds.includes(w.modifierWeightId));
  }, [fields.productVariantRows, fields.slot?.slotId]);

  const hasAnyVariants = useMemo(() =>
    (fields.productVariantRows ?? []).some((r: any) => (r.variants ?? []).length > 0),
    [fields.productVariantRows]
  );

  const noSharedSlots = hasAnyVariants && intersectedSlots !== null && intersectedSlots.length === 0;
  // --- end intersection helpers ---

  const renderFormFields = () => {
    if (!fields.operationType) return null;
    // Common price field for operations that need it
    const renderPriceField = () => {
      // Don't render price field in remove mode
      if (mode === 'remove') {
        return null;
      }
      return (
        <TextField
          // Cents, not dollars: this feeds authoringPrice.amount, a NonNegativeInt. Labelled and
          // previewed the same way as the named price field below, which takes the same units - a
          // bare "Price" behind a "$" invited dollars into a cents field.
          label='Price (cents)'
          type='number'
          value={fields.price}
          onChange={(e) => handlers.setPrice(e.target.value)}
          fullWidth
          margin='normal'
          inputProps={{ min: 0, step: 1 }}
          // Previewed through the same parser that validates, so the preview cannot vouch for an entry
          // the form would reject - "1.00" must not read back as $0.01.
          helperText={
            parseCentsInput(fields.price) === null
              ? 'Whole cents, e.g. 599 for $5.99'
              : formatCents(+fields.price)
          }
        />
      );
    };
    // Render fields based on operation type
    switch (fields.operationType) {
      case 'SetBundlePriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' ? 'Remove Bundle Price' : 'Set Bundle Price'}
            </Typography>
            <BundleSelect value={fields.bundle} onChange={handlers.setBundle} multiple={true} />
            {renderPriceField()}
          </>
        );
      case 'SetVariantPriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' ? 'Remove Variant Price' : 'Set Variant Price'}
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            {renderPriceField()}
          </>
        );
      case 'SetModifierWeightPriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' ? 'Remove Modifier Weight Price' : 'Set Modifier Weight Price'}
            </Typography>
            <ProductVariantRows
              rows={fields.productVariantRows}
              setRows={handlers.setProductVariantRows}
            />
            {noSharedSlots && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                The selected product variants share no common modifier slots.
              </Alert>
            )}
            <Box mt={2}>
              <SlotSelect
                value={fields.slot}
                onChange={handleSlotChange}
                slots={intersectedSlots ?? []}
              />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                modifiers={intersectedModifiers}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <WeightSelect
                value={fields.weights}
                onChange={handlers.setWeights}
                weights={intersectedWeights}
                multiple={true}
              />
            </Box>
            {renderPriceField()}
          </>
        );
      case 'SetNamedPriceAmountPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' ? 'Remove Named Price' : 'Set Named Price'}
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
              A named price applies to every product and variant that resolves to it, for each store in
              this patch. No product or variant selection is needed.
            </Typography>
            <Box mt={2}>
              <NamedPriceSelect value={fields.namedPrices} onChange={handlers.setNamedPrices} />
            </Box>
            {mode !== 'remove' && (
              <TextField
                label='Amount (cents)'
                type='number'
                value={fields.price}
                onChange={(e) => handlers.setPrice(e.target.value)}
                fullWidth
                margin='normal'
                inputProps={{ min: 0, step: 1 }}
                helperText={
                  parseCentsInput(fields.price) === null
                    ? 'Whole cents, e.g. 599 for $5.99'
                    : formatCents(+fields.price)
                }
              />
            )}
          </>
        );
      case 'SetModifierPriceRangesMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' ? 'Remove Modifier Price Ranges' : 'Set Modifier Price Ranges'}
            </Typography>
            <ProductVariantRows
              rows={fields.productVariantRows}
              setRows={handlers.setProductVariantRows}
            />
            {noSharedSlots && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                The selected product variants share no common modifier slots.
              </Alert>
            )}
            <Box mt={2}>
              <SlotSelect
                value={fields.slot}
                onChange={handleSlotChange}
                slots={intersectedSlots ?? []}
              />
            </Box>
            {mode !== 'remove' && (
              <Box mt={2}>
                <WeightPriceRanges ranges={fields.ranges} setRanges={handlers.setRanges} />
              </Box>
            )}
          </>
        );
      case 'RemoveProductMenuPatchOperation':
      case 'RemoveVariantMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {fields.operationType === 'RemoveProductMenuPatchOperation' ? 'Remove Product' : 'Remove Variant'}
            </Typography>
            <ProductSelect
              value={fields.product}
              onChange={handlers.setProduct}
              multiple={fields.operationType === 'RemoveProductMenuPatchOperation'}
              onProductChange={handleProductChange}
            />
            {fields.operationType === 'RemoveVariantMenuPatchOperation' && (
              <Box mt={2}>
                <VariantSelect
                  value={fields.variants}
                  onChange={handleVariantChange}
                  product={fields.product}
                  multiple={true}
                />
              </Box>
            )}
          </>
        );
      case 'RemoveBundleMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Bundle
            </Typography>
            <BundleSelect value={fields.bundle} onChange={handlers.setBundle} multiple={true} />
          </>
        );
      case 'RemoveModifierFromSlotPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Modifier From Slot
            </Typography>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} menu={fields.menu} />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
          </>
        );
      case 'RemoveModifierMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Modifier
            </Typography>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
          </>
        );
      case 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' 
                ? 'Remove Modifier Weight Exclusion From Variant' 
                : 'Exclude Modifier Weight From Variant'
              }
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} multiple={false} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} product={fields.product} />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <WeightSelect value={fields.weights} onChange={handlers.setWeights} product={fields.product} multiple={true} />
            </Box>
            {/* Only show checkbox for set operations, not removal operations */}
            {mode !== 'remove' && (
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fields.excludeFromVariant}
                      onChange={(e) => handlers.setExcludeFromVariant(e.target.checked)}
                    />
                  }
                  label='Exclude from variant'
                />
              </Box>
            )}
          </>
        );
      case 'RemoveWeightFromSlotModifierMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Weight From Slot Modifier
            </Typography>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} menu={fields.menu} />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <WeightSelect
                value={fields.weights}
                onChange={handlers.setWeights}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
          </>
        );
      default:
        return <Typography color='textSecondary'>This operation type is not yet supported in the form.</Typography>;
    }
  };
  return (
    <Box sx={{ mt: 3 }} data-testid="operationForm">
      <Divider sx={{ mb: 3 }} />
      {renderFormFields()}
    </Box>
  );
}
